/**
 * WorkspacePlanUpsellBanner — iter-170an follow-up.
 *
 * Surfaces a per-workspace upsell when the ACTIVE workspace has no
 * paid composition. Visible on the workspace's own dashboard (NOT
 * the global cross-spaces view). The banner is dismissable for the
 * session — we use sessionStorage so it returns on next browser
 * open but never nags within a single browsing session.
 *
 * Rendering rules:
 *   1. Skip while the composition query is loading.
 *   2. Skip when ``composition`` is truthy AND its primary_plan_id is
 *      set (i.e. the workspace has a paid plan).
 *   3. Skip when the user dismissed the banner this session.
 *
 * The CTA navigates to ``/pricing``. Pricing.jsx's workspace selector
 * defaults to ``activeSpaceId`` so the user lands on a pre-scoped
 * checkout in zero clicks.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Sparkles, X } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import { useSpace } from "../../contexts/SpaceContext";
import { tx } from "../../i18n";

const DISMISS_KEY_PREFIX = "lumfi_ws_upsell_dismissed:";

function readDismissed(spaceId) {
  try {
    return sessionStorage.getItem(DISMISS_KEY_PREFIX + spaceId) === "1";
  } catch {
    return false;
  }
}
function writeDismissed(spaceId) {
  try {
    sessionStorage.setItem(DISMISS_KEY_PREFIX + spaceId, "1");
  } catch { /* private mode */ }
}

export function WorkspacePlanUpsellBanner({ lang }) {
  const { activeSpaceId, activeSpace } = useSpace();
  // Re-render trigger when user dismisses (sessionStorage is opaque
  // to React state). We seed from the persisted value so the banner
  // stays dismissed across page navigation within the session.
  const [dismissed, setDismissed] = useState(() => readDismissed(activeSpaceId));

  const { data, isLoading } = useQuery({
    // The composition/me endpoint is space-scoped via the X-Space-Id
    // header (set automatically by the axios interceptor). We key the
    // cache by activeSpaceId so a workspace switch flushes cleanly.
    queryKey: ["composition-me", activeSpaceId],
    queryFn: async () => (await api.get("/billing/composition/me")).data,
    enabled: !!activeSpaceId,
    staleTime: 60_000,
    retry: false,
  });

  if (isLoading || dismissed) return null;
  const comp = data?.composition;
  // iter-184-2 — A workspace is "on free" when it has neither a paid
  // composition NOR an admin-granted plan. We now treat granted plans
  // as full plans for the chip/banner: courtesy users shouldn't see
  // the "upgrade" nag because they already have premium features.
  const hasPaidPlan = Boolean(comp && comp.primary_plan_id);
  const hasGrantedPlan = data?.plan?.source === "grant";
  if (hasPaidPlan || hasGrantedPlan) return null;

  const handleDismiss = () => {
    writeDismissed(activeSpaceId);
    setDismissed(true);
  };

  return (
    <Card
      className="mb-4 p-3 rounded-2xl border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50/60 flex items-center gap-3 flex-wrap"
      data-testid="workspace-plan-upsell-banner"
    >
      <div className="h-8 w-8 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0">
        <Sparkles className="h-4 w-4" />
      </div>
      <div className="text-xs text-amber-900 flex-1 min-w-[200px]">
        <strong data-testid="workspace-plan-upsell-title">
          {tx(
            lang,
            `Workspace${activeSpace?.name ? ` "${activeSpace.name}"` : ""} on Free`,
            `Workspace${activeSpace?.name ? ` "${activeSpace.name}"` : ""} en plan Free`,
            `Workspace${activeSpace?.name ? ` "${activeSpace.name}"` : ""} no plano Free`,
          )}
        </strong>
        <div className="mt-0.5 text-amber-800/85">
          {tx(
            lang,
            "Unlock Smart Alerts, advanced exports and more for this workspace.",
            "Desbloquea Smart Alerts, exportaciones avanzadas y más para este workspace.",
            "Desbloqueia Smart Alerts, exportações avançadas e mais para este workspace.",
          )}
        </div>
      </div>
      <Link to="/pricing" data-testid="workspace-plan-upsell-cta">
        <Button
          size="sm"
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
        >
          {tx(lang, "See plans", "Ver planes", "Ver planos")}
        </Button>
      </Link>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={handleDismiss}
        className="text-amber-700 hover:text-amber-900 p-1 rounded-md transition-colors"
        data-testid="workspace-plan-upsell-dismiss"
        title={tx(lang, "Dismiss", "Cerrar", "Fechar")}
      >
        <X className="h-4 w-4" />
      </button>
    </Card>
  );
}
